import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { PROJECT_STATUS } from '@features/project-management-tools/constents';
import {
	useDeleteLinkMutation,
	useUpdateOverviewAndStageVisibilityMutation,
	useUpdateProjectMutation
} from '@features/project-management-tools/api/projectManagementApi';
import {
	setIsEditProject,
	setIsShowCreateProjectModal,
	setSelectedProjectToEdit
} from '@features/project-management-tools/redux/myProjectSlice';
import CreateAndEditProjectModal from '@features/project-management-tools/components/CreateAndEditProjectModal';
import AddAndEditLinkModal from '@features/project-management-tools/components/AddAndEditLinkModal';
import { handleOpenLink } from '@features/project-management-tools/utils';

import LoaderComponent from '@/components/LoaderComponent';
import { toastError, toastSuccess } from '@/components/Toast';
import { getLoadingState, onGetLocalFromUtc, replaceUnderLine } from '@/utils/Util';
import CommonImage from '@/components/CommonImage';
import { useMyProject } from '@/store/reducers';
import CustomTooltip from '@/components/CustomTooltip';

const ProjectDetailsOverview = ({ projectDetails, refetch, projectId }) => {
	const [isShowLinkModal, setIsShowLinkModal] = useState(false);
	const [selectedLinkToEdit, setSelectedLinkToEdit] = useState(null);
	const [isHidden, setIsHidden] = useState(projectDetails?.isOverviewHidden || false);

	const dispatch = useDispatch();

	const { isShowCreateProjectModal, selectedProjectToEdit, isEditProject } = useMyProject();
	const [projectStatus, setProjectStatus] = useState('');
	const [updateProject, { isLoading: isUpdatingProject }] = useUpdateProjectMutation();
	const [deleteLink, { isLoading: isDeletingLink }] = useDeleteLinkMutation();
	const [updateOverviewAndStageVisibility] = useUpdateOverviewAndStageVisibilityMutation();

	const handleUpdateProjectStatus = (status) => {
		updateProject({
			body: { projectStatus: status },
			id: projectDetails?.id
		})
			.unwrap()
			.then(() => {
				toastSuccess('Project status updated successfully');
				refetch();
			})
			.catch((error) => {
				toastError(error?.data?.error);
			});
	};

	const handleDeleteLink = (id) => {
		deleteLink({ linkId: id })
			.unwrap()
			.then(() => {
				toastSuccess('Link deleted successfully');
				refetch();
			})
			.catch((error) => {
				toastError(error?.data?.error);
			});
	};

	useEffect(() => {
		setProjectStatus(projectDetails?.projectStatus);
	}, [projectDetails?.projectStatus]);

	useEffect(() => {
		setIsHidden(projectDetails?.isOverviewHidden || false);
	}, [projectDetails?.isOverviewHidden]);

	const toggleProjectDetails = () => {
		setIsHidden((prev) => !prev);
		updateOverviewAndStageVisibility({
			projectId,
			params: { isOverviewHidden: !isHidden }
		});
	};

	const isLoading = getLoadingState([isDeletingLink, isUpdatingProject]);

	return (
		<LoaderComponent loading={isLoading}>
			<div className="common-accordion">
				<div className="accordion" id="accordionExample">
					<div className="card mb-0">
						<div className="card-header" id="headingOne">
							<h2 className="mb-0">
								<button
									className="btn btn-link btn-block d-flex justify-content-between text-left gray_dark_01 regular-14 weight-600 p-0"
									type="button"
									onClick={toggleProjectDetails}
								>
									<div className="d-flex align-items-center justify-content-between">
										<p className="medium-14">Project overview</p>
										<span
											className="blue regular-14 ml-2 weight-400 cursor-pointer"
											onClick={(e) => {
												e.stopPropagation();
												dispatch(setIsShowCreateProjectModal(true));
												dispatch(setSelectedProjectToEdit(projectDetails));
												dispatch(setIsEditProject(true));
											}}
										>
											EDIT
										</span>
									</div>
									<div className="arrow-down">
										<CommonImage src="/icons/down-arrow-gray-dark-02.svg" alt="arrow" />
									</div>
								</button>
							</h2>
						</div>
						<div
							className={`collapse scroll-x-label${!isHidden ? ' show' : ''}`}
							aria-labelledby="headingOne"
							data-parent="#accordionExample"
						>
							<div className="card-body m-0 border-0">
								<div className="project-overview-content d-grid grid-columns-3">
									<div className="single-overview-content">
										<ul>
											<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center">
												<p className="title gray_dark_02">Project name:</p>
												<p className="value">{projectDetails?.name}</p>
											</li>
											<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center">
												<p className="title gray_dark_02">Project type:</p>
												<p className="value">
													{replaceUnderLine(projectDetails?.ProjectType)}
												</p>
											</li>
											<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center">
												<p className="title gray_dark_02">Brand:</p>
												<p className="value">{projectDetails?.brandResponse?.name}</p>
											</li>
											<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center">
												<p className="title gray_dark_02">Project status:</p>

												<div className="sort-button ml-0">
													<select
														name="status"
														value={projectStatus}
														onChange={(e) => {
															setProjectStatus(e.target.value);
															handleUpdateProjectStatus(e.target.value);
														}}
														className="border-0 bg-transparent py-0 gray_dark_01"
													>
														{PROJECT_STATUS.map((status) => (
															<option key={status.id} value={status.id}>
																{status.value}
															</option>
														))}
													</select>
												</div>
											</li>
											<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center">
												<p className="title gray_dark_02">Project progress:</p>
												<div className="progress-info task-progress-info mt-2 d-flex align-items-center justify-content-start">
													<div className="progress progress-square">
														<div
															className="progress-bar"
															style={{
																width: `${
																	(projectDetails?.noOfCompletedTask /
																		projectDetails?.noOfTotalTask) *
																	100
																}%`
															}}
														></div>
													</div>
													<div className="progress-text ml-2">
														<p className="regular-14 gray_dark_01">
															{projectDetails?.noOfCompletedTask}/
															{projectDetails?.noOfTotalTask}
														</p>
													</div>
												</div>
											</li>

											<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center">
												<p className="title gray_dark_02">Project owner:</p>
												<p className="value">{projectDetails?.ownerResponse?.name}</p>
											</li>
											<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center">
												<p className="title gray_dark_02">Creator:</p>
												<p className="value">
													{projectDetails?.createdByResponse?.name}
												</p>
											</li>
											<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center">
												<p className="title gray_dark_02">Project end date:</p>
												<p className="value">
													{onGetLocalFromUtc(
														projectDetails?.endDate,
														'DD-MMM, YYYY'
													)}
												</p>
											</li>
										</ul>
									</div>
									<div className="single-overview-content">
										<ul>
											<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center">
												<p className="title gray_dark_02">Buyer meeting date:</p>
												{projectDetails?.buyerFirstMeetingDate && (
													<p className="value">
														{onGetLocalFromUtc(
															projectDetails?.buyerFirstMeetingDate,
															'DD-MMM, YYYY'
														)}
													</p>
												)}
											</li>
											<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center">
												<p className="title gray_dark_02">Season:</p>
												<p className="value">{projectDetails?.season}</p>
											</li>
											{projectDetails?.styleCategoryDetails && (
												<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center">
													<p className="title gray_dark_02">Style category:</p>
													<p className="value">
														{projectDetails?.styleCategoryDetails}
													</p>
												</li>
											)}
											{projectDetails?.fabricDetails && (
												<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center">
													<p className="title gray_dark_02">Fabric details:</p>
													<p className="value">{projectDetails?.fabricDetails}</p>
												</li>
											)}
											{projectDetails?.note && (
												<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center">
													<p className="title gray_dark_02">Notes:</p>
													<p className="value">{projectDetails?.note}</p>
												</li>
											)}
										</ul>
									</div>
									{/* Link section */}
									<div className="single-overview-content">
										<ul className="link-list">
											<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center">
												<button
													className="button text size32 px-0"
													onClick={() => setIsShowLinkModal(true)}
												>
													<span className="ml-1 gray_dark_01 mr-2">
														Link <CommonImage src="/icons/add-round.svg" />
													</span>
												</button>
											</li>
											{projectDetails?.linkResponseList?.map((linkItem) => (
												<li
													className="regular-14 gray_dark_01 weight-500"
													key={linkItem?.id}
												>
													<div className="option">
														<div className="dropdown">
															<button
																className="btn btn-default dropdown-toggle"
																type="button"
																id="menu1"
																data-toggle="dropdown"
																aria-expanded="false"
															>
																<CommonImage
																	src="/icons/more_icon.svg"
																	alt=""
																/>
															</button>
															<ul
																className="dropdown-menu dropdown-menu-right shadow-lg"
																role="menu"
																aria-labelledby="menu1"
																x-placement="bottom-end"
															>
																<li
																	role="presentation"
																	className="px-4 pb-3 pt-3"
																>
																	<span
																		role="menuitem"
																		tabIndex={-1}
																		className="font-weight-normal  text-black"
																		onClick={() => {
																			setIsShowLinkModal(true);
																			setSelectedLinkToEdit(linkItem);
																		}}
																	>
																		Edit
																	</span>
																</li>
																<li
																	role="presentation"
																	className="px-4 pb-3"
																	onClick={() =>
																		handleDeleteLink(linkItem?.id)
																	}
																>
																	<span
																		role="menuitem"
																		tabIndex={-1}
																		className="font-weight-normal  text-black"
																	>
																		Delete
																	</span>
																</li>
															</ul>
														</div>
													</div>
													<div onClick={() => handleOpenLink(linkItem?.url)}>
														<CustomTooltip text={linkItem?.title}>
															<p className="title blue weight-400 text-underline cursor-pointer w-100">
																{linkItem?.title?.slice(0, 50)}
															</p>
														</CustomTooltip>
													</div>
												</li>
											))}
										</ul>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			{isShowCreateProjectModal && (
				<CreateAndEditProjectModal
					isShow={isShowCreateProjectModal}
					onClose={() => {
						dispatch(setSelectedProjectToEdit(null));
						dispatch(setIsShowCreateProjectModal(false));
					}}
					selectedProjectInfo={selectedProjectToEdit}
					isEdit={isEditProject}
					onUpdateProjectSuccess={() => {
						refetch();
					}}
				/>
			)}

			{isShowLinkModal && (
				<AddAndEditLinkModal
					onClose={() => setIsShowLinkModal(false)}
					onSubmitSuccess={() => {
						setSelectedLinkToEdit(null);
						refetch();
					}}
					selectedLinkToEdit={selectedLinkToEdit}
					projectId={projectId}
				/>
			)}
		</LoaderComponent>
	);
};

export default ProjectDetailsOverview;
